/* @jsx h */
import { defineComponent, ref, computed, h, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api } from "@app/data/api";
import { Loading, OrderFilters, OrderCard } from "@app/vue/components/index";
import { useAuth } from "@app/vue/providers/Auth";
import { Colleague, FoodType, Order, TableStatus } from "@lemmonhq/types";
import { setTitle } from "@lemmonhq/orders";
import { useShop } from "../providers";

type Filters = {
  status?: string;
  tableId?: number;
  serviceMethod?: string;
  productType?: string;
  staffId?: number;
};

export const OrdersPage = defineComponent({
  setup() {
    const loading = ref(true);
    const route = useRoute();
    const router = useRouter();
    const auth = useAuth();
    const shop = useShop();
    const { orders, tables, foodTypes } = shop;
    const isOrderSwapped = ref(false);

    onMounted(async () => {
      setTitle("Orders");
      try {
        await shop.refreshData();
      } finally {
        loading.value = false;
      }
    });

    const filters = ref<Filters>({
      status: route.query.status?.toString(),
      tableId: route.query.tableId ? Number(route.query.tableId) : undefined,
      serviceMethod: route.query.serviceMethod?.toString(),
      productType: route.query.productType?.toString(),
      staffId: route.query.staffId ? Number(route.query.staffId) : undefined,
    });

    const staffMembers = computed(() => {
      const allStaff: Colleague[] = [
        ...(auth.user.value ? [auth.user.value] : []),
        ...(auth.user.value?.colleagues || []),
      ].filter((user) => user?.role === "staff");
      return allStaff;
    });

    const filteredOrders = computed(() => {
      let list = orders.value
        .filter((order) => {
          if (order.status === "closed") return false;
          if (
            filters.value.status &&
            filters.value.status !== "all" &&
            order.status !== filters.value.status
          ) {
            return false;
          }

          if (
            filters.value.serviceMethod &&
            filters.value.serviceMethod !== "all" &&
            order.service_method !== filters.value.serviceMethod
          ) {
            return false;
          }

          if (
            filters.value.tableId &&
            order.table_id !== filters.value.tableId
          ) {
            return false;
          }

          if (
            filters.value.productType &&
            filters.value.productType !== "all"
          ) {
            const hasProductType = order.items?.some(
              (item) => item.product.type === filters.value.productType,
            );
            if (!hasProductType) return false;
          }

          if (
            filters.value.staffId &&
            order.user_id !== filters.value.staffId
          ) {
            return false;
          }

          return true;
        })
        .sort((orderA, orderB) => {
          const a = new Date(orderA.created_at);
          const b = new Date(orderB.created_at);
          return a.getTime() - b.getTime();
        });
      if (isOrderSwapped.value) {
        list = list.reverse();
      }
      return list;
    });

    const handleFoodStatusChange = async (
      orderId: number,
      foodTypeId: number,
      currentStatus: string,
    ) => {
      try {
        console.log("api");
        const resp = await api.changeFoodTypeStatus({
          orderID: orderId,
          foodTypeId: foodTypeId as number,
          currentStatus,
          type: "food_type",
        });
        console.log("handleStatusChange", resp);
        const { orders: updatedOrders, kitchenTickets } = resp;

        // Update orders ref with new data
        orders.value = orders.value.map((order) => {
          const match = updatedOrders.find((el) => el.id === order.id);
          return match ? match : order;
        });
        if (kitchenTickets && kitchenTickets.length > 0) {
          //
          try {
            Promise.all(
              kitchenTickets
                .filter((el) => el.doc.type === "kitchenTicket")
                .map(async (job) => shop.print(job)),
            );
          } catch (e) {
            console.log("error printing ticket", e);
          }
        }
      } catch (error) {
        console.error("Failed to update order status:", error);
      }
    };

    const handleOrderCancel = async (orderId: number) => {
      // Remove the order from the orders array
      orders.value = orders.value.filter((order) => order.id !== orderId);
    };

    const handleFilterChange = (newFilters: Filters) => {
      filters.value = newFilters;
      const query: Record<string, string> = {};
      if (newFilters.status) query.status = newFilters.status;
      if (newFilters.tableId) query.tableId = newFilters.tableId.toString();
      if (newFilters.serviceMethod)
        query.serviceMethod = newFilters.serviceMethod;
      if (newFilters.productType) query.productType = newFilters.productType;
      if (newFilters.staffId) query.staffId = newFilters.staffId.toString();
      router.replace({ query });
    };

    return () => {
      console.log("filteredOrders", filteredOrders.value.length);
      return loading.value ? (
        <div class="w-full h-full flex-1 flex justify-center items-center">
          <Loading />
        </div>
      ) : (
        <div class="flex flex-1 flex-col overflow-hidden p-2 px-4 py-4 lg:px-2 lg:py-2">
          <div
            style={"column-fill: auto"}
            class="overflow-x-auto flex-1 lg:columns-[200px]"
          >
            <OrderFilters
              tables={tables.value}
              staff={staffMembers.value}
              onFilterChange={handleFilterChange}
              initialFilters={filters.value}
              isOrderSwapped={isOrderSwapped}
            />
            {filteredOrders.value.map((order) => (
              <OrderCard
                key={order.id}
                orders={orders}
                order={order}
                foodTypes={foodTypes.value}
                onFoodStatusChange={handleFoodStatusChange}
                onOrderCancel={handleOrderCancel}
                onOrderUpdate={(changedOrder) => {
                  console.log("onOrderUpdate called", changedOrder);
                  orders.value = orders.value.map((order) => {
                    const match = changedOrder.id === order.id;
                    return match ? changedOrder : order;
                  });
                }}
                closeOpenOrder={async (order: Order) => {
                  const { orders: changedOrders } =
                    await api.closeOpenOrder(order);
                  orders.value = orders.value.map((order) => {
                    const match = changedOrders.find(
                      (el) => el.id === order.id,
                    );
                    return match ? match : order;
                  });
                  return true;
                }}
              />
            ))}
          </div>
        </div>
      );
    };
  },
});
